import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Select } from 'foundations-components/transitional/components/Select';
import { Map as ImmutableMap } from 'immutable';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { COMPANIES, CONTACTS, DEALS, ENGAGEMENT } from 'reporting-data/constants/dataTypes';
// @ts-expect-error Untyped dependency
import { isSupportedCrmObject } from 'reporting-data/crmObjects/utils';
import { getDateRangeFilterProperty, getReportConfig } from 'reporting-data/report/configReportGetters';
import { isExternalDataType } from 'reporting-data/report/externalReportGetters';
import { getConfigsIncludingPrimary } from 'reporting-data/report/multiConfigReportGetters';
import { getCustomWidgetType, getDataTypeFromConfig } from 'reporting-data/report/reportGetters';
import { CUSTOM_WIDGET_TYPES } from 'reporting-data/tsTypes/customWidgetTypes';
import { isAttributionReport, isConfigReport, isMultiConfigReport, isReportWithPlugins, isUnifiedAnalyticsReport, isUnifiedEventsQLReport, isUnifiedEventsReport } from 'reporting-data/tsTypes/reportTypes';
import { DateActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import { EVENTS } from 'reporting-ui-components/lib/events';
import useCloneTracker from 'reporting-ui-components/lib/useCloneTracker';
import { ReportType } from 'reporting-ui-components/types/ReportTypes';
import getIn from 'transmute/getIn';
import { ENABLE_DATE_PROPERTY_FOR_ALL_CONFIG_REPORTS } from '../../constants/gates';
import BaseSelectEditorComponent from './common/BaseSelectEditorComponent';
import { FILTER_BAR_SECTION_RIGHT_MARGIN_TRELLIS } from './common/constants';
import { useCustomObjectLabel } from './common/useCustomObjectLabel';
import { useDatePropertyEditorOptions } from './common/useDatePropertyEditorOptions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DATA_TYPES_SUPPORTED = [CONTACTS, COMPANIES, DEALS, ENGAGEMENT];
const getPipeline = getIn(['config', 'pipeline']);
const DatePropertyEditorView = ({
  report,
  options,
  onChange,
  appUsageTracker,
  config,
  configName,
  getPropertyGroups: customGetPropertyGroups,
  trackingAssetType,
  trackingApplicationName,
  trackingScreenName,
  isV2FilterExperience
}) => {
  const dateProperty = getDateRangeFilterProperty(config);
  const datePropertyOptions = useDatePropertyEditorOptions({
    report,
    config,
    getPropertyGroups: customGetPropertyGroups
  });
  const dataType = getDataTypeFromConfig(config);
  const customObjectLabel = useCustomObjectLabel({
    dataType
  });
  const dataTypeLabel = useMemo(() => isSupportedCrmObject(dataType) ? customObjectLabel.value : I18n.text(`reporting-ui-components.dataType.${dataType}.single`), [customObjectLabel.value, dataType]);
  const label = useMemo(() => {
    if (isMultiConfigReport(report)) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'settingLabels.dateProperty.labelWithDataType',
        options: {
          dataTypeLabel
        }
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: 'settingLabels.dateProperty.label'
    });
  }, [report, dataTypeLabel]);
  const usageTracker = useCloneTracker(appUsageTracker);
  const handleOnChange = useCallback(({
    target: {
      value: newProperty
    }
  }) => {
    usageTracker.track(EVENTS.changeSrvDateFilters, {
      action: DateActionTypes.DATE_PROPERTY,
      assetType: trackingAssetType || 'report',
      applicationName: trackingApplicationName,
      whereScreen: trackingScreenName,
      isNewFilterExperience: isV2FilterExperience
    });
    if (configName === 'primary') {
      return onChange(report.setIn(['config', 'filters', 'dateRange', 'property'], newProperty));
    }
    return onChange(report.setIn(['reportConfigs', configName, 'filters', 'dateRange', 'property'], newProperty));
  }, [configName, isV2FilterExperience, onChange, report, trackingApplicationName, trackingAssetType, trackingScreenName, usageTracker]);
  if (datePropertyOptions.status === 'pending') {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  if (datePropertyOptions.status === 'rejected') {
    throw datePropertyOptions.error;
  }
  const readOnly = options.readOnly;
  if (isV2FilterExperience) {
    return /*#__PURE__*/_jsx(BaseSelectEditorComponent, {
      tooltipHeader: label,
      "data-test-id": "date-property-select",
      options: datePropertyOptions.value,
      value: dateProperty,
      onChange: handleOnChange
    });
  }
  return /*#__PURE__*/_jsx(FormControl, {
    label: label,
    children: /*#__PURE__*/_jsx(Select, {
      "data-test-id": "date-property-select",
      buttonUse: readOnly ? 'unstyled' : 'form',
      disabled: readOnly,
      options: datePropertyOptions.value,
      value: dateProperty,
      onChange: handleOnChange
    })
  });
};
DatePropertyEditorView.propTypes = {
  appUsageTracker: PropTypes.object,
  report: ReportType.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({
    readOnly: PropTypes.bool
  }).isRequired,
  config: PropTypes.object,
  configName: PropTypes.string,
  getPropertyGroups: PropTypes.func
};
const DatePropertyEditor = props => {
  const {
    report,
    isV2FilterExperience
  } = props;
  const configs = isMultiConfigReport(report) ? getConfigsIncludingPrimary(report) : ImmutableMap({
    primary: getReportConfig(report)
  });
  const datePropertyEditors = configs.keySeq().map(configName => {
    return /*#__PURE__*/_jsx(DatePropertyEditorView, Object.assign({}, props, {
      config: configs.get(configName),
      configName: configName
    }), configName);
  });
  return isV2FilterExperience ? /*#__PURE__*/_jsx(Flex, {
    gap: FILTER_BAR_SECTION_RIGHT_MARGIN_TRELLIS,
    children: datePropertyEditors
  }) : /*#__PURE__*/_jsx(_Fragment, {
    children: datePropertyEditors
  });
};
DatePropertyEditor.isUsableForReport = (report, gates) => {
  // this is to prevent reports without a config pro (eg: journey) from moving further
  // UE and UA reports do not take the date property into account
  // UEQL is a new repot that isn't yet tested
  // Attribution changing the date property is not undesirable for UX reasons
  if (!isConfigReport(report) && !isReportWithPlugins(report) || isUnifiedAnalyticsReport(report) || isUnifiedEventsReport(report) || isUnifiedEventsQLReport(report) || isAttributionReport(report)) {
    return false;
  }
  const widgetType = getCustomWidgetType(report);
  const incompaticalCustomWidgetTypes = [CUSTOM_WIDGET_TYPES.CHANGE_HISTORY_DEAL_LIST, CUSTOM_WIDGET_TYPES.WATERFALL, CUSTOM_WIDGET_TYPES.DEAL_PUSH_ANALYSIS, CUSTOM_WIDGET_TYPES.PIPELINE_SNAPSHOTS, CUSTOM_WIDGET_TYPES.ACTIVITY_FEED];
  if (widgetType && incompaticalCustomWidgetTypes.includes(widgetType)) {
    return false;
  }
  const dataType = getDataTypeFromConfig(getReportConfig(report));
  if (gates && gates.includes(ENABLE_DATE_PROPERTY_FOR_ALL_CONFIG_REPORTS)) {
    if (dataType === 'EMAIL') {
      /* EMAIL reports are fulfilled via a cosemail API that doesn't take date property into account*/
      return false;
    }
    return isReportWithPlugins(report) || isExternalDataType(dataType) || getPipeline(report) == null;
  }
  return isReportWithPlugins(report) || isExternalDataType(dataType) || dataType != null && DATA_TYPES_SUPPORTED.includes(dataType) && getPipeline(report) == null;
};
DatePropertyEditor.isDateEditor = true;
DatePropertyEditor.isFilterEditor = true;
export default DatePropertyEditor;